import { FileIdsValue, ProjectDiff, SelectMultipleValue } from '@kaizenplatform/hugot-entity';
import gql from 'graphql-tag';

import GraphQLClient, { GraphQLOperationFailed } from '../../adapter/GraphQLClient';
import { GetProjectByIdQuery, GetProjectChangeQuery } from '../../graphql/generated/graphql';
import { jsonToDynamicSchemaValue } from '../../utility/graphql/project';
import { DigNonNullable } from '../../utilityTypes/DigNonNullable';

export type ProjectChangeType =
  | (NonNullable<GetProjectChangeQuery['projectChange']> & {
      latestVersion: Pick<ProjectDiff, 'creativeBrief' | 'assetCategory'> & {
        creativeBriefValue?: {
          definition: DigNonNullable<GetProjectByIdQuery, ['project', 'creativeBriefDefinition']>;
          values: Map<string, string | number | boolean | SelectMultipleValue | FileIdsValue | null>;
        };
        assetCategoryValue?: {
          definition: DigNonNullable<GetProjectByIdQuery, ['project', 'assetCategoryDefinition']>;
          values: Map<string, string | number | boolean | SelectMultipleValue | FileIdsValue | null>;
        };
      };
      beforeVersion: Pick<ProjectDiff, 'creativeBrief' | 'assetCategory'> & {
        creativeBriefValue?: {
          definition: DigNonNullable<GetProjectByIdQuery, ['project', 'creativeBriefDefinition']>;
          values: Map<string, string | number | boolean | SelectMultipleValue | FileIdsValue | null>;
        };
        assetCategoryValue?: {
          definition: DigNonNullable<GetProjectByIdQuery, ['project', 'assetCategoryDefinition']>;
          values: Map<string, string | number | boolean | SelectMultipleValue | FileIdsValue | null>;
        };
      };
    })
  | null;

class ProjectChangeWebApi {
  private readonly graphqlClient: GraphQLClient;

  constructor({ graphqlClient }: { graphqlClient: GraphQLClient }) {
    this.graphqlClient = graphqlClient;
  }

  public getById = async (
    project: Pick<
      NonNullable<GetProjectByIdQuery['project']>,
      'id' | 'creativeBriefDefinition' | 'assetCategoryDefinition'
    >,
  ): Promise<ProjectChangeType> => {
    try {
      const result: GetProjectChangeQuery = await this.graphqlClient.query(
        gql`
          query getProjectChange($projectId: ID!) {
            projectChange(projectId: $projectId) {
              updatedAt
              latestVersion {
                creativeBrief
                assetCategory
              }
              beforeVersion {
                creativeBrief
                assetCategory
              }
            }
          }
        `,
        { projectId: project.id },
      );
      if (result.projectChange) {
        const projectChange = result.projectChange as NonNullable<ProjectChangeType>;

        projectChange.latestVersion.creativeBriefValue = jsonToDynamicSchemaValue(
          projectChange.latestVersion.creativeBrief,
          project.creativeBriefDefinition,
        );
        projectChange.latestVersion.assetCategoryValue = jsonToDynamicSchemaValue(
          projectChange.latestVersion.assetCategory,
          project.assetCategoryDefinition,
        );
        projectChange.beforeVersion.creativeBriefValue = jsonToDynamicSchemaValue(
          projectChange.beforeVersion.creativeBrief,
          project.creativeBriefDefinition,
        );
        projectChange.beforeVersion.assetCategoryValue = jsonToDynamicSchemaValue(
          projectChange.beforeVersion.assetCategory,
          project.assetCategoryDefinition,
        );

        return projectChange;
      } else {
        return null;
      }
    } catch (err) {
      if (err instanceof GraphQLOperationFailed) {
        throw err;
      }
      throw err;
    }
  };
}

export default ProjectChangeWebApi;
